import React, { useState, useEffect, useContext } from 'react'
import { IAccount } from 'types/userInterfaces'
import { ITeam } from 'types/teamInterfaces'
import { useLogin } from './login'
import { userAPI } from 'api/UserAPI'
import { networkAPI } from 'api/NetworkAPI'
import { personAPI } from 'api/PersonAPI'
import { adminAPI } from '../api/AdminAPI'
import { teamAPI } from 'api/TeamAPI'
import { IError } from 'types/error'

interface UserState {
  ready: boolean
  user: IAccount | null
  groups: string[]
  networkAccess: boolean
  profileImage: string
  features: string[]
  isTeamLead: boolean
  setRefresh: (refresh: boolean) => void
  groupsReady: boolean
  update: boolean
  setUpdate: (update: boolean) => void
  setUpdateUser: (updateUser: boolean) => void
  error: IError | undefined
}

// The default value's type is a lie here because the default value is
// never actually used! We always give the unexported Provider a valid
// value as a prop
const userContext = React.createContext<UserState>({} as UserState)

const { Provider } = userContext

/**
 * Provider for sharing user context through the application.
 *
 * @param children
 * @returns Provider for accessing user context.
 */
export const UserProvider: React.FC<Record<string, unknown>> = ({ children }) => {
  const [user, setUser] = useState<IAccount | null>(null)
  const [groups, setGroups] = useState<string[]>([])
  const [ready, setReady] = useState<boolean>(false)
  const [networkAccess, setNetworkAccess] = useState<boolean>(false)
  const [profileImage, setProfileImage] = useState<string>('')
  const [features, setFeatures] = useState<string[]>([])
  const [isTeamLead, setIsTeamLead] = useState<boolean>(false)
  const [refresh, setRefresh] = useState<boolean>(false)
  const [groupsReady, setGroupsReady] = useState<boolean>(false)
  const [update, setUpdate] = useState<boolean>(false)
  const [updateUser, setUpdateUser] = useState<boolean>(false)
  const [error, setError] = useState<IError>()

  const { loggedIn, userId } = useLogin()

  useEffect(() => {
    const controller = new AbortController()

    if ((update || loggedIn) && userId !== null) {
      ;(async () => {
        try {
          setReady(false)

          const [newUser, networkAccessData, featuresData] = await Promise.all([
            userAPI.getUserData(userId, controller),
            networkAPI.checkAccess(controller),
            adminAPI.getUserFeatures(controller),
          ])
          setUser(newUser)
          setGroups(Array.from(newUser.Groups.map(group => group.type)))
          setNetworkAccess(networkAccessData)
          setFeatures(featuresData)
          setGroupsReady(true)
          setReady(true)
          setRefresh(true)
          setUpdate(false)
        } catch (err) {
          setUser(null)
          setGroups([])
          setNetworkAccess(false)
          setGroupsReady(false)
          setReady(false)
          setError(err as IError)
        }
      })()
    } else {
      setUser(null)
      setGroups([])
      setGroupsReady(false)
      // The "initial" fetch is now done
      setReady(true)
      setRefresh(true)
      setUpdate(false)
    }

    return () => {
      controller.abort()
    }
  }, [loggedIn, userId, update])

  useEffect(() => {
    const controller = new AbortController()

    ;(async () => {
      if (ready && user) {
        const teamsWhereLead: ITeam[] = await teamAPI.getTeamsWhereManager(controller)
        setIsTeamLead(teamsWhereLead && teamsWhereLead.length > 0 ? true : false)
      }
    })()

    return () => {
      controller.abort()
    }
  }, [user, ready])

  useEffect(() => {
    const controller = new AbortController()

    ;(async () => {
      if (refresh && ready) {
        if (user && user.Person) {
          // Load profile image in background without blocking ready state
          personAPI
            .getProfileImage(user.Person.id, controller)
            .then(image => {
              {
                setProfileImage(image)
              }
            })
            .catch(() => {
              if (!controller.signal.aborted) {
                setProfileImage('')
              }
            })
            // reset only after loading, resetting earlier re-runs this effect and aborts the request
            .finally(() => {
              if (!controller.signal.aborted) {
                setRefresh(false)
              }
            })
        } else {
          setProfileImage('')
          setRefresh(false)
        }
      }
    })()

    return () => {
      controller.abort()
    }
  }, [user, refresh, ready])

  useEffect(() => {
    const controller = new AbortController()

    if (userId && updateUser) {
      ;(async () => {
        const result = await userAPI.getUserData(userId, controller)
        setUser(result)
        setUpdateUser(false)
      })()
    }

    return () => {
      controller.abort()
    }
  }, [updateUser, userId])

  return React.createElement(
    Provider,
    {
      value: {
        ready,
        user,
        groups,
        networkAccess,
        profileImage,
        features,
        isTeamLead,
        setRefresh,
        groupsReady,
        update,
        setUpdate,
        setUpdateUser,
        error,
      },
    },
    children as React.ReactNode
  )
}

/**
 * Hook for getting user state.
 *
 * @returns User context variables and functions.
 */
export function useUser(): UserState {
  return useContext(userContext)
}
